import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AppContext } from 'src/components/app-context';
import RouteContext from 'src/routes/context';
import { SecondarySearchField } from 'src/components/secondary-search-field';
import FilterSection, {
  findGroupedSections,
  findUngroupedSections,
  isValidSection,
} from '../filter-section';
import { FilterOverlay } from 'src/components/filter-overlay';
import { SectionItems } from 'src/components/filter-overlay/components/section-items';
import { AllFilterSections } from './all-filter-sections';
import FilterSectionGroup from './group';
import { useGlobalPropertySearch } from './global-property-search';
import { useAggregationSettings } from '../../hooks';
import { ProductCollectionFilterContext } from '../../filter-tracking';
import type { AggregationSection, ProductCollectionFilter } from 'src/types';
import type { ProductCollectionNonNameSearchFilter } from './types';
import * as S from './styles';
import { FilterContext } from '../../filter-context';
import { formatFilterFn } from '../../utils/filter';

type Props = {
  appliedFilters: ProductCollectionNonNameSearchFilter[];
  clearFilter: (property?: string) => void;
  filters: ProductCollectionFilter[];
  productsCount: number;
  sections: AggregationSection[];
  className?: string;
};

const mobileOverlayContextValue = { filterLayout: 'mobile-overlay' as const };

export const FilterMobile = (props: Props) => {
  const { t, market } = useContext(AppContext);
  const { mobileFilterOpen, setMobileFilterOpen, setCount } =
    useContext(FilterContext);
  const { appliedFilters, clearFilter, filters, productsCount, sections } =
    props;

  const { dispatchAggregationAction } = useContext(RouteContext);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [currentSectionId, setCurrentSectionId] =
    useState<AggregationSection['id']>();

  const aggregationSettings = useAggregationSettings(currentSectionId || '');

  const currentSection = useMemo(
    () => sections.find(({ id }) => id === currentSectionId),
    [sections, currentSectionId]
  );

  const toggleExpandedGroup = useCallback(
    (id: string) => {
      if (expandedGroups.includes(id)) {
        setExpandedGroups(expandedGroups.filter(groupId => groupId !== id));
      } else {
        setExpandedGroups([...expandedGroups, id]);
      }
    },
    [expandedGroups]
  );

  const onBack = useCallback(() => {
    if (aggregationSettings?.query) {
      dispatchAggregationAction({
        type: 'AGGREGATION_SEARCH',
        id: currentSectionId,
        query: '',
      });
    }
    setCurrentSectionId(undefined);
  }, [dispatchAggregationAction, aggregationSettings, currentSectionId]);

  const onClose = useCallback(() => {
    setMobileFilterOpen(false);
    onBack();
  }, []);

  const onClear = useCallback(() => {
    // If currentSection is undefined (index view is open), all filters will be cleared.
    // Otherwise, just the filter for that property will be cleared.
    if (currentSection) {
      clearFilter(currentSection.property);
    } else {
      clearFilter();
    }
  }, [currentSection, clearFilter]);

  const getCallToActionText = (option: { property?: string }) => {
    const filter = appliedFilters.find(
      item => item.property === option.property
    );

    if (!filter) return undefined;

    return formatFilterFn({ filter, t }).value;
  };

  const ungroupedSections = useMemo(
    () => findUngroupedSections(sections),
    [sections]
  );

  const sectionGroups = useMemo(
    () => findGroupedSections(sections, appliedFilters, market.code),
    [sections, appliedFilters, market.code]
  );

  const canClear = currentSection
    ? appliedFilters.some(
        ({ property }) => property === currentSection.property
      )
    : appliedFilters.length > 0;

  const {
    isSearching,
    filterSearchQuery,
    filterByPropertySearch,
    hasSectionMatchingFilter,
    onSecondarySearchFieldChange,
  } = useGlobalPropertySearch();

  useEffect(() => {
    setCount(filters.length);
  }, [setCount, filters]);

  return mobileFilterOpen ? (
    <ProductCollectionFilterContext.Provider value={mobileOverlayContextValue}>
      <FilterOverlay
        allSections={
          <AllFilterSections
            sections={sections}
            filters={appliedFilters}
            isSearching={isSearching}
            filterByPropertySearch={filterByPropertySearch}
            hasSectionMatchingFilter={hasSectionMatchingFilter}
          />
        }
        canClear={canClear}
        clearFilter={onClear}
        count={productsCount}
        currentSection={
          currentSection &&
          isValidSection(currentSection) && (
            <FilterSection
              section={currentSection}
              useInfiniteScroll
              appliedFilters={appliedFilters}
            />
          )
        }
        hasFiltered={appliedFilters.length > 0}
        isSearching={isSearching}
        onBack={onBack}
        onClose={onClose}
        searchInput={
          <S.FilterSearchWrapper>
            <SecondarySearchField
              value={filterSearchQuery}
              onChange={onSecondarySearchFieldChange}
              clearText={t('common', 'button.clear')}
              placeholder={t('filter', 'properties.search-placeholder')}
            />
          </S.FilterSearchWrapper>
        }
        sectionGroups={
          <>
            {sectionGroups.map(group => (
              <FilterSectionGroup
                key={group.title}
                group={group}
                expanded={expandedGroups.includes(group.title) || isSearching}
                toggleExpanded={toggleExpandedGroup}
              >
                <SectionItems
                  filters={group.sections}
                  getCallToActionText={getCallToActionText}
                  setCurrentSectionId={setCurrentSectionId}
                />
              </FilterSectionGroup>
            ))}
          </>
        }
        sectionItems={
          <SectionItems
            filters={ungroupedSections}
            getCallToActionText={getCallToActionText}
            setCurrentSectionId={setCurrentSectionId}
          />
        }
        showBackArrow={Boolean(currentSection)}
        showInitialView={!currentSection}
        title={
          currentSection ? currentSection.title : t('filter', 'filter.title')
        }
      />{' '}
    </ProductCollectionFilterContext.Provider>
  ) : null;
};
